import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@/types';
import { relativeTime } from '@/utils/format';

export default function HeroCarousel({ articles }: { articles: Article[] }) {
  const [index, setIndex] = useState(0);
  const count = articles.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 5000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const go = (i: number) => setIndex((i + count) % count);

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden bg-foreground-950">
      {articles.map((item, i) => {
        const TitleTag = i === index ? 'h1' : 'h2';
        return (
        <Link
          key={item.id}
          to={`/detail/${item.id}`}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          aria-hidden={i !== index}
        >
          <img src={item.cover} alt={item.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
          <div className="absolute inset-0 w-full flex items-end">
            <div className="w-full px-4 md:px-8 pb-16 md:pb-20">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium mb-4">
                <i className="ri-fire-line" /> 今日焦点 · {item.categoryName}
              </span>
              <TitleTag className="font-heading text-2xl md:text-4xl font-bold text-white max-w-4xl leading-snug text-balance">
                {item.title}
              </TitleTag>
              <p className="hidden md:block mt-3 text-base text-white/85 max-w-3xl line-clamp-2">{item.summary}</p>
              <div className="flex items-center gap-4 mt-5 text-sm text-white/75">
                <span className="flex items-center gap-1"><i className="ri-time-line" /> {relativeTime(item.publishTime)}</span>
                <span className="flex items-center gap-1"><i className="ri-user-line" /> {item.author}</span>
              </div>
            </div>
          </div>
        </Link>
        );
      })}

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一条"
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 text-white backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一条"
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 text-white backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute left-1/2 -translate-x-1/2 bottom-6 flex items-center gap-2">
        {articles.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`第 ${i + 1} 条`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-6 bg-primary-500' : 'w-2 bg-white/50 hover:bg-white/80'}`}
          />
        ))}
      </div>
    </section>
  );
}